// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Button, InlineLoading, InlineNotification, Tile } from '@carbon/react';
import PublishTargetCard, { type TargetDecision } from '../components/PublishTargetCard';
import BypassedItemsTable from '../components/BypassedItemsTable';
import { useCoverageStaging } from '../../../hooks/useCoverageStaging';
import { applyApproval } from '../../../services/coverageService';

interface ReviewApproveStepProps {
  siteId: string;
  sessionId: string;
  onBack: () => void;
}

/**
 * Step 3 — "Review & approve": per-target publish decisions over the
 * staged results. Decisions apply per target and application is idempotent
 * downstream, so a partial failure keeps failed targets decidable and a
 * re-apply covers only unresolved work. When every target publishes, the
 * session completes and the site lifecycle flips to active.
 */
export default function ReviewApproveStep({ siteId, sessionId, onBack }: ReviewApproveStepProps) {
  const { staging, loading, error, refetch } = useCoverageStaging(siteId, sessionId, true);
  const [decisions, setDecisions] = useState<Record<string, TargetDecision>>({});
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [lastSessionStatus, setLastSessionStatus] = useState<string | null>(null);

  if (loading && !staging) return <InlineLoading description="Loading staged results..." />;
  if (!staging) {
    return (
      <InlineNotification
        kind="error"
        title="Staged results unavailable"
        subtitle={error ?? 'The session has no staging summary yet.'}
        lowContrast
      />
    );
  }

  const actionable = staging.actions.filter(
    action => action.status === 'pending' || action.status === 'failed'
  );
  const undecided = actionable.filter(action => !decisions[action.target]);
  const allPublished =
    staging.actions.length > 0 && staging.actions.every(action => action.status === 'done');

  const decide = (target: string, decision: TargetDecision) =>
    setDecisions(prev => ({ ...prev, [target]: decision }));

  const apply = async () => {
    setApplying(true);
    setApplyError(null);
    try {
      const response = await applyApproval(siteId, sessionId, {
        targets: actionable.map(action => ({
          target: action.target,
          decision: decisions[action.target],
        })),
      });
      setLastSessionStatus(response.session_status);
      setDecisions({});
    } catch (err) {
      setApplyError((err as Error).message);
    } finally {
      setApplying(false);
      refetch();
    }
  };

  const collected = [
    { value: staging.equipment.length, label: 'Equipment units confirmed' },
    {
      value: Object.values(staging.documents_by_target).reduce(
        (total, docs) => total + docs.length,
        0
      ),
      label: 'Documents routed to targets',
    },
    { value: staging.rollup?.satisfied ?? 0, label: 'Checklist items satisfied' },
    { value: staging.bypasses.length, label: 'Bypassed with reason' },
  ];

  return (
    <div className="obw-step">
      <p className="obw-review-note">
        Nothing below is live yet. Approving publishes each target; rejecting returns its items to
        the checklist.
      </p>

      {applyError && (
        <InlineNotification
          kind="error"
          title="Applying decisions failed"
          subtitle={applyError}
          lowContrast
          onCloseButtonClick={() => setApplyError(null)}
        />
      )}
      {lastSessionStatus && !applyError && (
        <InlineNotification
          kind={lastSessionStatus === 'published' ? 'success' : 'info'}
          title={
            lastSessionStatus === 'published'
              ? 'All targets published — onboarding complete'
              : `Session status: ${lastSessionStatus.replace(/_/g, ' ')}`
          }
          lowContrast
          hideCloseButton
        />
      )}

      <h4 className="obw-table-title">Publish targets</h4>
      <div className="obw-target-grid">
        {staging.actions.map(action => (
          <PublishTargetCard
            key={action.action_id}
            action={action}
            documents={staging.documents_by_target[action.target] ?? []}
            decision={decisions[action.target]}
            onDecide={decide}
            disabled={applying}
          />
        ))}
      </div>

      <h4 className="obw-table-title">What was collected</h4>
      <div className="obw-stat-tiles">
        {collected.map(tile => (
          <Tile key={tile.label} className="obw-stat-tile">
            <span className="obw-stat-value">{tile.value}</span>
            <span className="obw-stat-label">{tile.label}</span>
          </Tile>
        ))}
      </div>

      <BypassedItemsTable bypasses={staging.bypasses} />

      <p className="obw-rules-note">
        Decisions apply per target; a rejected target returns its documents to review while approved
        targets stay published. Applying decisions is idempotent, so a failed publish resumes on
        retry. When every target is approved and published, the site lifecycle flips to active and
        onboarding completes.
      </p>

      <div className="obw-step-actions">
        <Button kind="secondary" onClick={onBack} disabled={applying}>
          Back to documents
        </Button>
        <Button
          onClick={apply}
          disabled={applying || allPublished || actionable.length === 0 || undecided.length > 0}
        >
          Apply decisions
        </Button>
        {applying && <InlineLoading description="Applying decisions..." />}
      </div>
    </div>
  );
}
